import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Logo from '../components/Logo';
import InventoryTab from '../components/InventoryTab';
import BillModal from '../components/BillModal';
import {
  getOrders, updateOrderStatus,
  getMenu, addMenuItem, updateMenuItem, deleteMenuItem,
  updateMe,
  getTables, addTable, deleteTable,
} from '../api';
import { useAuth } from '../context/AuthContext';

const POLL_MS    = 5000;
const CATEGORIES = [
  // Food service
  'Beverages','Snacks','Breakfast','Starters','Main Course',
  'Rice & Breads','Thali & Combos','Lunch','Dinner','Desserts',
  // Kirana / grocery
  'Groceries','Dairy & Eggs','Pulses & Grains','Masala & Spices',
  'Oils & Ghee','Packed Foods','Fruits & Vegetables',
  // Other
  'Personal Care','Stationery','General',
];

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  return `${Math.floor(s/3600)}h ago`;
}

// ── Print Bill ────────────────────────────────────────────────────────────────
function printBill(order, shopName) {
  const dt = new Date(order.created_at).toLocaleString();
  const rows = order.items.map((i) =>
    `<tr><td>${i.name}</td><td style="text-align:center">${i.quantity}</td>` +
    `<td style="text-align:right">₹${i.price}</td>` +
    `<td style="text-align:right">₹${(i.price * i.quantity).toFixed(2)}</td></tr>`
  ).join('');
  const copyHtml = (label) => `
    <div class="copy">
      <div class="shop-name">${shopName || 'Shop'}</div>
      <div class="order-num">Order #${order.id}</div>
      <div class="info-row">Customer: <b>${order.customer_name || 'Guest'}</b></div>
      ${order.table_no ? `<div class="info-row">📍 Table: <b>${order.table_no}</b></div>` : ''}
      <div class="info-row">Date: ${dt}</div>
      ${order.notes ? `<div class="notes-box">📝 ${order.notes}</div>` : ''}
      <table class="items-table">
        <thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th>Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="subtotal">Subtotal: ₹${order.total_amount}</div>
      <div class="grand-total">TOTAL: ₹${order.total_amount}</div>
      <div class="copy-label">${label}</div>
    </div>`;
  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Bill #${order.id}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;padding:20px;background:#f5f5f5}
      .copies{display:flex;gap:0;flex-wrap:wrap;justify-content:center}
      .copy{background:#fff;border:1px solid #ccc;border-radius:8px;padding:20px;
            width:280px;margin:10px;flex-shrink:0}
      .copies .copy+.copy{border-left:2px dotted #999}
      .shop-name{font-size:18px;font-weight:700;text-align:center;margin-bottom:4px}
      .order-num{font-size:14px;text-align:center;color:#555;margin-bottom:10px;
                 padding-bottom:8px;border-bottom:1px solid #eee}
      .info-row{font-size:12px;margin-bottom:4px;color:#333}
      .notes-box{background:#fef9c3;border:1px solid #fde047;border-radius:4px;
                 padding:6px 8px;font-size:12px;margin:6px 0}
      .items-table{width:100%;border-collapse:collapse;margin:10px 0;font-size:12px}
      .items-table th{background:#f3f4f6;padding:4px 6px;text-align:left;border-bottom:1px solid #ddd}
      .items-table td{padding:4px 6px;border-bottom:1px solid #f0f0f0}
      .subtotal{text-align:right;font-size:12px;color:#555;margin-top:6px}
      .grand-total{text-align:right;font-size:15px;font-weight:700;margin-top:4px;
                   padding-top:6px;border-top:2px solid #333}
      .copy-label{text-align:center;font-size:11px;color:#888;margin-top:12px;
                  padding-top:8px;border-top:1px dashed #ccc;letter-spacing:.5px}
      .print-btn{display:block;margin:16px auto;padding:10px 32px;background:#4338ca;
                 color:#fff;border:none;border-radius:8px;font-size:15px;cursor:pointer}
      @media print{.print-btn{display:none}.copies{flex-direction:column;align-items:center}}
    </style></head>
    <body>
      <div class="copies">
        ${copyHtml('CUSTOMER COPY')}
        ${copyHtml('RESTAURANT COPY')}
      </div>
      <button class="print-btn" onclick="window.print()">Print</button>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`);
  win.document.close();
}

// ── Order Card ────────────────────────────────────────────────────────────────
function OrderCard({ order, onAction, business, onSendBill }) {
  const summary = order.items.map((i) => `${i.name} ×${i.quantity}`).join('  ·  ');
  const cls     = order.status.toLowerCase();
  return (
    <div className={`order-card ${cls}`}>
      <div className="order-card-header">
        <span className="order-number">#{order.id}</span>
        <div className="order-meta">
          <span className={`status-badge ${cls}`}>{order.status}</span>
          {order.order_type && order.order_type !== 'dine-in' && (
            <span style={{fontSize:10,background:'rgba(245,158,11,0.12)',border:'1px solid rgba(245,158,11,0.35)',borderRadius:12,padding:'2px 8px',marginLeft:6,color:'#fcd34d',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.4px'}}>{order.order_type}</span>
          )}
          <div className="order-time">{timeAgo(order.created_at)}</div>
        </div>
      </div>
      <div className="order-items-summary">{summary}</div>
      <div className="order-amount">₹{order.total_amount}</div>
      <div style={{fontSize:12,color:'#94a3b8',marginTop:6}}>
        <span>👤 {order.customer_name || 'Guest'}</span>
        {order.customer_email && <span style={{marginLeft:10,color:'#6366f1'}}>✉️ {order.customer_email}</span>}
        {order.table_no && <span style={{marginLeft:10}}>📍 {order.table_no}</span>}
      </div>
      {order.notes && (
        <div style={{background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:6,
                     padding:'5px 9px',fontSize:12,marginTop:6,color:'#fcd34d'}}>
          📝 {order.notes}
        </div>
      )}
      <div style={{marginTop:8,display:'flex',gap:8,flexWrap:'wrap'}}>
        {order.status === 'PENDING' && (
          <button className="accept-btn" onClick={() => onAction(order.id,'ACCEPTED')}>
            Accept Order
          </button>
        )}
        {order.status === 'ACCEPTED' && (
          <button className="complete-btn" style={{background:'#0ea5e9'}}
            onClick={() => onAction(order.id,'SERVED')}>
            Mark as Served
          </button>
        )}
        {order.status === 'SERVED' && (
          <button className="complete-btn" style={{background:'#f59e0b'}}
            onClick={() => onAction(order.id,'PAYMENT_PENDING')}>
            Request Payment
          </button>
        )}
        {order.status === 'PAYMENT_PENDING' && (
          <button className="complete-btn" style={{background:'#10b981'}}
            onClick={() => onAction(order.id,'COMPLETED')}>
            Payment Received — Mark as Paid
          </button>
        )}
        <button
          onClick={() => onSendBill(order)}
          style={{padding:'7px 14px',fontSize:12,background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.35)',
                  borderRadius:8,cursor:'pointer',fontWeight:600,color:'#a5b4fc'}}>
          ✉️ Send Bill
        </button>
        <button
          onClick={() => printBill(order, business?.name)}
          style={{padding:'7px 14px',fontSize:12,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.12)',
                  borderRadius:8,cursor:'pointer',fontWeight:500,color:'#cbd5e1'}}>
          🖨 Print
        </button>
      </div>
    </div>
  );
}

// ── Menu Item Row ─────────────────────────────────────────────────────────────
function MenuItemRow({ item, onSave, onDelete }) {
  const [editing,      setEditing]      = useState(false);
  const [form,         setForm]         = useState({ ...item });
  const [saving,       setSaving]       = useState(false);
  const [togglingSold, setTogglingSold] = useState(false);

  const f = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target.type==='checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    await onSave(item.id, { ...form, price: parseFloat(form.price) });
    setSaving(false); setEditing(false);
  };

  if (editing) return (
    <div className="menu-edit-card">
      <div className="menu-edit-row"><label>Name</label>
        <input className="menu-input" value={form.name} onChange={f('name')} /></div>
      <div className="menu-edit-row"><label>Description</label>
        <input className="menu-input" value={form.description} onChange={f('description')} placeholder="Optional" /></div>
      <div className="menu-edit-row"><label>Price (₹)</label>
        <input className="menu-input" type="number" min="0" step="0.5" value={form.price} onChange={f('price')} /></div>
      <div className="menu-edit-row"><label>Category</label>
        <select className="menu-input" value={form.category} onChange={f('category')}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
      <div className="menu-edit-row"><label>Visible on menu</label>
        <input type="checkbox" checked={form.active} onChange={f('active')} style={{width:22,height:22}} /></div>
      <div className="menu-edit-row"><label>Best Seller</label>
        <input type="checkbox" checked={!!form.bestseller} onChange={f('bestseller')} style={{width:22,height:22}} /></div>
      <div className="menu-edit-actions">
        <button className="btn-save" onClick={save} disabled={saving}>{saving?'Saving…':'Save'}</button>
        <button className="btn-cancel" onClick={() => { setForm({...item}); setEditing(false); }}>Cancel</button>
      </div>
    </div>
  );

  const toggleSold = async () => {
    setTogglingSold(true);
    await onSave(item.id, { ...item, active: !item.active });
    setTogglingSold(false);
  };

  return (
    <div className={`menu-item-row ${item.active?'':'inactive'}`}>
      <div className="menu-item-info">
        <span className="mi-name">{item.bestseller ? '⭐ ' : ''}{item.name}</span>
        <span className="mi-cat">{item.category}</span>
        {!item.active && <span className="mi-hidden">Hidden</span>}
      </div>
      <div className="menu-item-price">₹{item.price}</div>
      <div className="menu-item-actions">
        <button
          onClick={toggleSold}
          disabled={togglingSold}
          style={{
            fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none',
            cursor: togglingSold ? 'not-allowed' : 'pointer', fontWeight: 600,
            background: item.active ? '#22c55e' : '#ef4444',
            color: '#fff', marginRight: 4,
          }}
        >
          {item.active ? 'Available ✓' : 'Not Available ✗'}
        </button>
        <button className="btn-edit" onClick={() => { setForm({...item}); setEditing(true); }}>Edit</button>
        <button className="btn-delete" onClick={() => onDelete(item.id)}>✕</button>
      </div>
    </div>
  );
}

// ── Add Item Form ─────────────────────────────────────────────────────────────
const EMPTY = { name:'', description:'', price:'', category:'General', active:true, bestseller:false };
function AddItemForm({ onAdd }) {
  const [open,  setOpen]  = useState(false);
  const [form,  setForm]  = useState(EMPTY);
  const [saving,setSaving]= useState(false);
  const [err,   setErr]   = useState('');
  const f = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target.type==='checkbox' ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.price) { setErr('Name and price are required.'); return; }
    setSaving(true); setErr('');
    await onAdd({ ...form, price: parseFloat(form.price) });
    setForm(EMPTY); setSaving(false); setOpen(false);
  };

  if (!open) return (
    <button className="add-item-btn" onClick={() => setOpen(true)}>+ Add New Item</button>
  );

  return (
    <div className="menu-edit-card" style={{borderLeft:'5px solid #FF6B35'}}>
      <div style={{fontWeight:700,marginBottom:10,fontSize:15}}>New Menu Item</div>
      {err && <div style={{color:'#c0392b',fontSize:13,marginBottom:8}}>{err}</div>}
      <div className="menu-edit-row"><label>Name *</label>
        <input className="menu-input" value={form.name} onChange={f('name')} placeholder="e.g. Masala Chai" /></div>
      <div className="menu-edit-row"><label>Description</label>
        <input className="menu-input" value={form.description} onChange={f('description')} placeholder="Optional" /></div>
      <div className="menu-edit-row"><label>Price (₹) *</label>
        <input className="menu-input" type="number" min="0" step="0.5" value={form.price} onChange={f('price')} placeholder="0" /></div>
      <div className="menu-edit-row"><label>Category</label>
        <select className="menu-input" value={form.category} onChange={f('category')}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
      <div className="menu-edit-row"><label>Visible on menu</label>
        <input type="checkbox" checked={!!form.active} onChange={f('active')} style={{width:22,height:22}} /></div>
      <div className="menu-edit-row"><label>Best Seller</label>
        <input type="checkbox" checked={!!form.bestseller} onChange={f('bestseller')} style={{width:22,height:22}} /></div>
      <div className="menu-edit-actions">
        <button className="btn-save" onClick={submit} disabled={saving}>{saving?'Adding…':'Add Item'}</button>
        <button className="btn-cancel" onClick={() => { setOpen(false); setForm(EMPTY); }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Shop Settings Tab ─────────────────────────────────────────────────────────
function ShopSettings({ business, onUpdate }) {
  const [form,    setForm]    = useState({
    name:        business.name        || '',
    phone:       business.phone       || '',
    address:     business.address     || '',
    description: business.description || '',
    gst_percent: business.gst_percent != null ? String(business.gst_percent) : '0',
  });
  const [pwForm,  setPwForm]  = useState({ currentPassword:'', newPassword:'', confirmPassword:'' });
  const [saving,  setSaving]  = useState(false);
  const [pwSaving,setPwSaving]= useState(false);
  const [msg,     setMsg]     = useState('');
  const [pwMsg,   setPwMsg]   = useState('');

  const shopUrl    = `${window.location.origin}/shop/${business.businessId}`;
  const kitchenUrl = `${window.location.origin}/kitchen/${business.businessId}`;

  const printQR = () => {
    const win = window.open('', '_blank', 'width=520,height=720');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>QR — ${business.name}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;
             background:#fff;display:flex;flex-direction:column;
             align-items:center;padding:40px 20px}
        .card{border:2px solid #e5e7eb;border-radius:16px;
              padding:32px 28px;max-width:340px;width:100%;text-align:center}
        h2{font-size:22px;font-weight:700;margin-bottom:6px;color:#111}
        .tag{color:#666;font-size:14px;margin-bottom:24px}
        img{display:block;margin:0 auto 20px}
        .url{font-size:10px;color:#aaa;word-break:break-all;margin-bottom:20px}
        .brand{font-size:11px;color:#4338ca;font-weight:600;letter-spacing:.3px}
        .btn{margin-top:24px;padding:10px 36px;background:#4338ca;
             color:#fff;border:none;border-radius:8px;font-size:15px;cursor:pointer}
        @media print{.btn{display:none}}
      </style></head><body>
      <div class="card">
        <h2>${business.name}</h2>
        <div class="tag">Scan to view menu &amp; order</div>
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=10&data=${encodeURIComponent(shopUrl)}"
             width="260" height="260" alt="QR code"/>
        <div class="url">${shopUrl}</div>
        <div class="brand">Powered by Scanningo.in</div>
      </div>
      <button class="btn" onclick="window.print()">Print</button>
    </body></html>`);
    win.document.close();
  };

  const saveInfo = async () => {
    setSaving(true); setMsg('');
    try {
      await updateMe({ ...form, gst_percent: parseFloat(form.gst_percent) || 0 });
      setMsg('Saved!'); onUpdate();
    }
    catch (e) { setMsg(e.message); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg('Passwords do not match.'); return;
    }
    setPwSaving(true); setPwMsg('');
    try {
      await updateMe({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwMsg('Password updated!');
      setPwForm({ currentPassword:'', newPassword:'', confirmPassword:'' });
    } catch (e) { setPwMsg(e.message); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="orders-section">
      {/* Business ID card */}
      <div className="biz-id-display" style={{marginBottom:16}}>
        <div className="biz-id-label">Your Business ID (use this to log in)</div>
        <div className="biz-id-value">{business.businessId}</div>
      </div>

      {/* QR code + customer link */}
      <div className="qr-url-box" style={{marginBottom:20,textAlign:'center'}}>
        <div className="qr-url-label" style={{marginBottom:12}}>
          Customer Menu QR — print and place on tables
        </div>
        <div style={{display:'inline-block',background:'#fff',padding:12,
                     borderRadius:12,border:'1px solid rgba(255,255,255,0.1)',marginBottom:12}}>
          <QRCode value={shopUrl} size={160} />
        </div>
        <div className="qr-url-value" style={{marginBottom:10,wordBreak:'break-all'}}>
          {shopUrl}
        </div>
        <div style={{display:'flex',gap:8,justifyContent:'center',flexWrap:'wrap'}}>
          <button className="btn-copy"
            onClick={() => navigator.clipboard.writeText(shopUrl)}>
            Copy Link
          </button>
          <button className="btn-save" style={{padding:'8px 18px',fontSize:13}}
            onClick={printQR}>
            Print QR
          </button>
        </div>
      </div>

      {/* Kitchen View link */}
      <div style={{background:'#1a1d2e',border:'1px solid #312e81',borderRadius:12,
                   padding:'16px 18px',marginBottom:20}}>
        <div style={{fontWeight:700,fontSize:13,color:'#818cf8',marginBottom:6}}>
          👨‍🍳 Kitchen Display Screen
        </div>
        <div style={{fontSize:12,color:'#94a3b8',marginBottom:12,lineHeight:1.5}}>
          Open this link on a tablet or screen in your kitchen. It shows all accepted orders in real-time — no login required.
        </div>
        <div style={{wordBreak:'break-all',fontSize:11,color:'#6366f1',marginBottom:10}}>
          {kitchenUrl}
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn-copy"
            onClick={() => navigator.clipboard.writeText(kitchenUrl)}>
            Copy Kitchen Link
          </button>
          <button className="btn-save" style={{padding:'8px 18px',fontSize:13}}
            onClick={() => window.open(kitchenUrl, '_blank')}>
            Open Kitchen View
          </button>
        </div>
      </div>

      {/* Shop info */}
      <div className="settings-section-title">Shop Information</div>
      {msg && <div className={`settings-msg ${msg==='Saved!'?'ok':'err'}`}>{msg}</div>}

      <div className="menu-edit-row"><label>Shop Name</label>
        <input className="menu-input" value={form.name}
          onChange={(e) => setForm((p) => ({...p, name: e.target.value}))} /></div>
      <div className="menu-edit-row"><label>Description</label>
        <input className="menu-input" value={form.description}
          onChange={(e) => setForm((p) => ({...p, description: e.target.value}))} placeholder="Optional tagline" /></div>
      <div className="menu-edit-row"><label>Phone</label>
        <input className="menu-input" value={form.phone}
          onChange={(e) => setForm((p) => ({...p, phone: e.target.value}))} /></div>
      <div className="menu-edit-row"><label>Address</label>
        <input className="menu-input" value={form.address}
          onChange={(e) => setForm((p) => ({...p, address: e.target.value}))} /></div>
      <div className="menu-edit-row">
        <label>GST % <span style={{fontSize:11,color:'#64748b',fontWeight:400}}>(shown on bill — 0 to hide)</span></label>
        <input className="menu-input" type="number" min="0" max="100" step="0.1"
          value={form.gst_percent}
          onChange={(e) => setForm((p) => ({...p, gst_percent: e.target.value}))}
          placeholder="e.g. 5 or 18" />
      </div>

      <button className="btn-save" style={{width:'100%',marginBottom:28}} onClick={saveInfo} disabled={saving}>
        {saving ? 'Saving…' : 'Save Changes'}
      </button>

      {/* Password change */}
      <div className="settings-section-title">Change Password</div>
      {pwMsg && <div className={`settings-msg ${pwMsg==='Password updated!'?'ok':'err'}`}>{pwMsg}</div>}

      <div className="menu-edit-row"><label>Current Password</label>
        <input className="menu-input" type="password"
          value={pwForm.currentPassword}
          onChange={(e) => setPwForm((p) => ({...p, currentPassword: e.target.value}))} /></div>
      <div className="menu-edit-row"><label>New Password</label>
        <input className="menu-input" type="password"
          value={pwForm.newPassword}
          onChange={(e) => setPwForm((p) => ({...p, newPassword: e.target.value}))} /></div>
      <div className="menu-edit-row"><label>Confirm New Password</label>
        <input className="menu-input" type="password"
          value={pwForm.confirmPassword}
          onChange={(e) => setPwForm((p) => ({...p, confirmPassword: e.target.value}))} /></div>

      <button className="btn-save" style={{width:'100%'}} onClick={changePassword} disabled={pwSaving}>
        {pwSaving ? 'Updating…' : 'Update Password'}
      </button>
    </div>
  );
}

// ── Stats Tab ─────────────────────────────────────────────────────────────────
function StatsTab({ orders }) {
  const today = new Date().toISOString().slice(0, 10);
  const completed = orders.filter((o) => o.status === 'COMPLETED');
  const todayOrders = orders.filter((o) => o.created_at && o.created_at.slice(0, 10) === today);
  const todayRevenue = completed
    .filter((o) => o.created_at && o.created_at.slice(0, 10) === today)
    .reduce((s, o) => s + o.total_amount, 0);
  const totalRevenue = completed.reduce((s, o) => s + o.total_amount, 0);
  const pendingCount   = orders.filter((o) => o.status === 'PENDING').length;
  const acceptedCount  = orders.filter((o) => o.status === 'ACCEPTED').length;
  const completedCount = completed.length;
  const avgOrder = completedCount > 0 ? (totalRevenue / completedCount) : 0;

  // Top items
  const itemMap = {};
  orders.forEach((o) => {
    (o.items || []).forEach((i) => {
      if (!itemMap[i.name]) itemMap[i.name] = 0;
      itemMap[i.name] += i.quantity || 1;
    });
  });
  const topItems = Object.entries(itemMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, qty]) => ({ name, qty }));
  const top5 = topItems.slice(0, 5);
  const medals = ['🥇','🥈','🥉','4️⃣','5️⃣'];

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' });
    const dayRevenue = completed
      .filter((o) => o.created_at?.slice(0, 10) === key)
      .reduce((s, o) => s + o.total_amount, 0);
    const dayOrders = orders.filter((o) => o.created_at?.slice(0, 10) === key).length;
    return { date: label, revenue: Math.round(dayRevenue), orders: dayOrders };
  });

  const card = (label, value) => (
    <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'14px 18px',
                 border:'1px solid rgba(255,255,255,0.08)',textAlign:'center'}}>
      <div style={{fontSize:11,color:'#64748b',marginBottom:4,textTransform:'uppercase',letterSpacing:.4}}>{label}</div>
      <div style={{fontSize:22,fontWeight:700,color:'#f1f5f9'}}>{value}</div>
    </div>
  );

  const chartCard = (title, children) => (
    <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,border:'1px solid rgba(255,255,255,0.08)',
                 padding:'16px 18px',marginBottom:16}}>
      <div style={{fontWeight:700,fontSize:14,marginBottom:12,color:'#e2e8f0'}}>{title}</div>
      {children}
    </div>
  );

  return (
    <div className="orders-section">
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:12,marginBottom:20}}>
        {card("Today's Revenue", `₹${todayRevenue.toFixed(0)}`)}
        {card('Total Revenue', `₹${totalRevenue.toFixed(0)}`)}
        {card('Orders Today', todayOrders.length)}
        {card('Pending', pendingCount)}
        {card('Accepted', acceptedCount)}
        {card('Completed', completedCount)}
        {card('Avg Order', `₹${avgOrder.toFixed(0)}`)}
      </div>

      {chartCard('Revenue — Last 7 Days',
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={last7Days}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill:'#64748b' }} />
            <YAxis tick={{ fontSize: 10, fill:'#64748b' }} />
            <Tooltip contentStyle={{background:'#1e2535',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#e2e8f0'}} formatter={(v) => ['₹' + v, 'Revenue']} />
            <Area dataKey="revenue" stroke="#6366f1" fill="rgba(99,102,241,0.15)" name="Revenue (₹)" />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {topItems.length > 0 && chartCard('Top Items',
        <ResponsiveContainer width="100%" height={Math.max(180, topItems.length * 36)}>
          <BarChart layout="vertical" data={topItems}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis type="number" tick={{ fontSize: 10, fill:'#64748b' }} />
            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill:'#94a3b8' }} />
            <Tooltip contentStyle={{background:'#1e2535',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#e2e8f0'}} />
            <Bar dataKey="qty" fill="#FF6B35" name="Sold" />
          </BarChart>
        </ResponsiveContainer>
      )}

      {chartCard('Orders — Last 7 Days',
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={last7Days}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill:'#64748b' }} />
            <YAxis tick={{ fontSize: 10, fill:'#64748b' }} />
            <Tooltip contentStyle={{background:'#1e2535',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,color:'#e2e8f0'}} />
            <Bar dataKey="orders" fill="#22c55e" name="Orders" />
          </BarChart>
        </ResponsiveContainer>
      )}

      {top5.length > 0 && (
        <div style={{background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'16px 18px',
                     border:'1px solid rgba(255,255,255,0.08)'}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12,color:'#e2e8f0'}}>Top Items</div>
          {top5.map(({ name, qty }, idx) => (
            <div key={name} style={{display:'flex',justifyContent:'space-between',
                                    padding:'6px 0',borderBottom:'1px solid rgba(255,255,255,0.06)',fontSize:14,color:'#cbd5e1'}}>
              <span>{medals[idx]} {name}</span>
              <span style={{color:'#94a3b8',fontWeight:600}}>{qty} sold</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Table Map Tab ─────────────────────────────────────────────────────────────
function TableMapTab({ orders, business }) {
  const [tables,    setTables]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [addForm,   setAddForm]   = useState({ name: '', section: 'Main', capacity: 4 });
  const [addErr,    setAddErr]    = useState('');
  const [adding,    setAdding]    = useState(false);

  const fetchTables = useCallback(async () => {
    setLoading(true);
    try { setTables(await getTables()); }
    catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  const activeMap = {};
  orders
    .filter((o) => ['PENDING','ACCEPTED','SERVED','PAYMENT_PENDING'].includes(o.status) && o.table_no?.trim())
    .forEach((o) => { activeMap[o.table_no.trim()] = o; });

  const sections = [...new Set(tables.map((t) => t.section))];

  const handleAdd = async () => {
    if (!addForm.name.trim()) { setAddErr('Name is required.'); return; }
    setAdding(true); setAddErr('');
    try {
      await addTable({ ...addForm, capacity: parseInt(addForm.capacity, 10) || 4 });
      setAddForm({ name: '', section: 'Main', capacity: 4 });
      await fetchTables();
    } catch (e) { setAddErr(e.message); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this table?')) return;
    try { await deleteTable(id); await fetchTables(); }
    catch {}
  };

  const printTableQR = (table) => {
    const url = window.location.origin + '/shop/' + business.businessId + '?table=' + encodeURIComponent(table.name);
    const win = window.open('', '_blank', 'width=520,height=720');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
      <title>QR — ${table.name}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:-apple-system,BlinkMacSystemFont,sans-serif;
             background:#fff;display:flex;flex-direction:column;
             align-items:center;padding:40px 20px}
        .card{border:2px solid #e5e7eb;border-radius:16px;
              padding:32px 28px;max-width:340px;width:100%;text-align:center}
        h2{font-size:22px;font-weight:700;margin-bottom:6px;color:#111}
        .tag{color:#666;font-size:14px;margin-bottom:24px}
        img{display:block;margin:0 auto 20px}
        .url{font-size:10px;color:#aaa;word-break:break-all;margin-bottom:20px}
        .brand{font-size:11px;color:#4338ca;font-weight:600;letter-spacing:.3px}
        .btn{margin-top:24px;padding:10px 36px;background:#4338ca;
             color:#fff;border:none;border-radius:8px;font-size:15px;cursor:pointer}
        @media print{.btn{display:none}}
      </style></head><body>
      <div class="card">
        <h2>${business.name}</h2>
        <div class="tag">Table: ${table.name} — Scan to order</div>
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=10&data=${encodeURIComponent(url)}"
             width="260" height="260" alt="QR code"/>
        <div class="url">${url}</div>
        <div class="brand">Powered by Scanningo.in</div>
      </div>
      <button class="btn" onclick="window.print()">Print</button>
    </body></html>`);
    win.document.close();
  };

  if (loading) return <div className="loading">Loading tables…</div>;

  return (
    <div className="orders-section">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',gap:16,fontSize:12,color:'#64748b'}}>
          <span style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{width:14,height:14,borderRadius:3,background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',display:'inline-block'}}></span> Empty
          </span>
          <span style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{width:14,height:14,borderRadius:3,background:'rgba(59,130,246,0.2)',border:'1px solid #3b82f6',display:'inline-block'}}></span> Running
          </span>
        </div>
        <button
          onClick={() => setShowSetup((v) => !v)}
          style={{padding:'6px 14px',fontSize:12,fontWeight:600,background: showSetup ? 'rgba(255,255,255,0.08)' : '#4338ca',
                  color: showSetup ? '#94a3b8' : '#fff',border:'1px solid rgba(255,255,255,0.1)',borderRadius:8,cursor:'pointer'}}>
          {showSetup ? 'Close Setup' : 'Setup Tables'}
        </button>
      </div>

      {showSetup && (
        <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:10,padding:'14px 16px',marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'#e2e8f0'}}>Add Table</div>
          {addErr && <div style={{color:'#f87171',fontSize:13,marginBottom:8}}>{addErr}</div>}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
            <input
              placeholder="Name *"
              value={addForm.name}
              onChange={(e) => setAddForm((p) => ({...p, name: e.target.value}))}
              style={{flex:'1 1 100px',padding:'7px 10px',border:'1px solid rgba(255,255,255,0.12)',borderRadius:7,fontSize:13,outline:'none',background:'rgba(255,255,255,0.06)',color:'#e2e8f0'}}
            />
            <input
              placeholder="Section"
              value={addForm.section}
              onChange={(e) => setAddForm((p) => ({...p, section: e.target.value}))}
              style={{flex:'1 1 80px',padding:'7px 10px',border:'1px solid rgba(255,255,255,0.12)',borderRadius:7,fontSize:13,outline:'none',background:'rgba(255,255,255,0.06)',color:'#e2e8f0'}}
            />
            <input
              type="number"
              placeholder="Capacity"
              min={1}
              value={addForm.capacity}
              onChange={(e) => setAddForm((p) => ({...p, capacity: e.target.value}))}
              style={{width:80,padding:'7px 10px',border:'1px solid rgba(255,255,255,0.12)',borderRadius:7,fontSize:13,outline:'none',background:'rgba(255,255,255,0.06)',color:'#e2e8f0'}}
            />
            <button
              onClick={handleAdd}
              disabled={adding}
              style={{padding:'7px 18px',background:'#FF6B35',color:'#fff',border:'none',borderRadius:7,fontSize:13,fontWeight:600,cursor:'pointer'}}>
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
          <div style={{fontWeight:700,fontSize:13,marginBottom:8,color:'#e2e8f0'}}>Existing Tables</div>
          {tables.length === 0 && <div style={{fontSize:13,color:'#64748b'}}>No tables yet.</div>}
          {tables.map((t) => (
            <div key={t.id} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.06)',fontSize:13,color:'#cbd5e1'}}>
              <span style={{flex:1}}><b style={{color:'#f1f5f9'}}>{t.name}</b> — {t.section}, {t.capacity} seats</span>
              <button
                onClick={() => printTableQR(t)}
                style={{padding:'3px 10px',fontSize:11,background:'#4338ca',color:'#fff',border:'none',borderRadius:6,cursor:'pointer',fontWeight:600}}>
                Print QR
              </button>
              <button
                onClick={() => handleDelete(t.id)}
                style={{padding:'3px 8px',fontSize:13,background:'rgba(239,68,68,0.15)',color:'#f87171',border:'1px solid rgba(239,68,68,0.3)',borderRadius:6,cursor:'pointer',fontWeight:700}}>
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {sections.length === 0 && (
        <div className="empty-state">No tables configured. Use "Setup Tables" to add tables.</div>
      )}

      {sections.map((section) => (
        <div key={section} style={{marginBottom:20}}>
          <div className="section-heading">{section}</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:12,marginTop:8}}>
            {tables.filter((t) => t.section === section).map((t) => {
              const active = activeMap[t.name];
              return (
                <div
                  key={t.id}
                  style={{
                    width:90,height:80,borderRadius:10,
                    border: active ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
                    background: active ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                    display:'flex',flexDirection:'column',alignItems:'center',
                    justifyContent:'center',padding:6,position:'relative',cursor:'default',
                    boxShadow: active ? '0 0 12px rgba(59,130,246,0.2)' : 'none',
                  }}
                  title={active ? `${active.customer_name || 'Guest'} — ₹${active.total_amount}` : 'Empty'}
                >
                  {active && (
                    <span style={{position:'absolute',top:5,right:6,width:7,height:7,
                                   borderRadius:'50%',background:'#3b82f6',boxShadow:'0 0 6px #3b82f6'}}></span>
                  )}
                  <div style={{fontWeight:700,fontSize:13,textAlign:'center',lineHeight:1.2,color: active ? '#e2e8f0' : '#94a3b8'}}>{t.name}</div>
                  <div style={{fontSize:10,color:'#475569',marginTop:2}}>{t.capacity} seats</div>
                  {active && (
                    <div style={{fontSize:10,color:'#60a5fa',fontWeight:600,marginTop:3}}>₹{active.total_amount}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Vendor Dashboard (main) ───────────────────────────────────────────────────
export default function VendorDashboard() {
  const { business, logout, refreshBusiness } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');

  // Orders
  const [orders,    setOrders]   = useState([]);
  const [ordersOK,  setOrdersOK] = useState(false);
  const [lastUp,    setLastUp]   = useState(null);
  const [orderErr,  setOrderErr] = useState('');
  const [billOrder, setBillOrder] = useState(null);

  // Menu
  const [menuItems, setMenu]     = useState([]);
  const [menuLoad,  setMenuLoad] = useState(false);
  const [menuErr,   setMenuErr]  = useState('');

  // New-order sound
  const prevOrderIds = useRef(null);
  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
    } catch {}
  };

  // ── Orders polling ────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    try {
      const newOrders = await getOrders();
      if (prevOrderIds.current !== null) {
        const newPending = newOrders.filter(
          (o) => o.status === 'PENDING' && !prevOrderIds.current.has(o.id)
        );
        if (newPending.length > 0) beep();
      }
      prevOrderIds.current = new Set(newOrders.map((o) => o.id));
      setOrders(newOrders);
      setLastUp(new Date());
    }
    catch { /* silent */ } finally { setOrdersOK(true); }
  }, []);

  useEffect(() => {
    fetchOrders();
    const id = setInterval(fetchOrders, POLL_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  const handleOrderAction = async (id, status) => {
    setOrderErr('');
    try { await updateOrderStatus(id, status); await fetchOrders(); }
    catch { setOrderErr(`Failed to update #${id}. Try again.`); }
  };

  // ── Menu ─────────────────────────────────────────────────────────────────
  const fetchMenu = useCallback(async () => {
    setMenuLoad(true);
    try { setMenu(await getMenu()); setMenuErr(''); }
    catch { setMenuErr('Could not load menu.'); }
    finally { setMenuLoad(false); }
  }, []);

  useEffect(() => { if (tab === 'menu') fetchMenu(); }, [tab, fetchMenu]);

  const handleSaveItem   = async (id, data) => { await updateMenuItem(id, data); await fetchMenu(); };
  const handleAddItem    = async (data)      => { await addMenuItem(data);        await fetchMenu(); };
  const handleDeleteItem = async (id) => {
    if (!window.confirm('Remove this item from the menu?')) return;
    await deleteMenuItem(id); await fetchMenu();
  };

  const pending        = orders.filter((o) => o.status === 'PENDING');
  const accepted       = orders.filter((o) => o.status === 'ACCEPTED');
  const served         = orders.filter((o) => o.status === 'SERVED');
  const paymentPending = orders.filter((o) => o.status === 'PAYMENT_PENDING');
  const completed      = orders.filter((o) => o.status === 'COMPLETED');

  const [isFS, setIsFS] = useState(false);
  useEffect(() => {
    const h = () => setIsFS(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', h);
    document.addEventListener('webkitfullscreenchange', h);
    return () => { document.removeEventListener('fullscreenchange', h); document.removeEventListener('webkitfullscreenchange', h); };
  }, []);
  const toggleFS = () => {
    if (isFS) (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    else { const el = document.documentElement; (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el); }
  };
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || !!window.navigator.standalone;

  return (
    <div className="vendor-page">
      {/* ── Header ── */}
      <div className="vendor-header">
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <Logo size="sm" showText={false} />
          <div>
            <h1>{business?.name || 'Dashboard'}</h1>
            <div style={{fontSize:11,color:'#aaa',marginTop:1}}>{business?.businessId}</div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          {tab === 'orders' && lastUp && (
            <span className="refresh-info">{timeAgo(lastUp.toISOString())}</span>
          )}
          {!isStandalone && (
            isIOS
              ? <button title="Tap Share → Add to Home Screen" className="fs-btn"
                  style={{background:'rgba(255,255,255,0.1)',border:'none',color:'#fff',padding:'6px 10px',borderRadius:8,fontSize:14,cursor:'pointer',display:'none'}}>⊕</button>
              : <button onClick={toggleFS} className="fs-btn" title={isFS ? 'Exit fullscreen' : 'Fullscreen'}
                  style={{background:'rgba(255,255,255,0.1)',border:'none',color: isFS ? '#FF6B35' : '#fff',padding:'6px 10px',borderRadius:8,fontSize:15,cursor:'pointer',display:'none'}}>
                  {isFS ? '⊠' : '⛶'}
                </button>
          )}
          <button onClick={() => { logout(); navigate('/vendor/login'); }}
            style={{background:'rgba(255,255,255,0.1)',border:'none',color:'#fff',
                    padding:'6px 12px',borderRadius:8,fontSize:13,cursor:'pointer'}}>
            Logout
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tab-bar">
        <button className={`tab-btn ${tab==='orders'?'active':''}`} onClick={() => setTab('orders')}>
          Orders {pending.length > 0 && <span className="tab-badge">{pending.length}</span>}
        </button>
        <button className={`tab-btn ${tab==='menu'?'active':''}`} onClick={() => setTab('menu')}>
          Menu
        </button>
        <button className={`tab-btn ${tab==='settings'?'active':''}`} onClick={() => setTab('settings')}>
          Shop
        </button>
        <button className={`tab-btn ${tab==='stats'?'active':''}`} onClick={() => setTab('stats')}>
          Stats
        </button>
        <button className={`tab-btn ${tab==='tables'?'active':''}`} onClick={() => setTab('tables')}>
          Tables
        </button>
        <button className={`tab-btn ${tab==='inventory'?'active':''}`} onClick={() => setTab('inventory')}>
          Inventory
        </button>
      </div>

      {/* ══════ ORDERS ══════ */}
      {tab === 'orders' && (
        <div className="orders-section">
          {orderErr && (
            <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,
                         padding:'10px 14px',marginBottom:12,fontSize:14,color:'#f87171'}}>
              {orderErr}
            </div>
          )}
          {!ordersOK ? <div className="loading">Loading orders…</div> : (
            <>
              <div className="section-heading">Pending · {pending.length}</div>
              {pending.length === 0
                ? <div className="empty-state">No pending orders</div>
                : pending.map((o) => <OrderCard key={o.id} order={o} onAction={handleOrderAction} business={business} onSendBill={setBillOrder} />)}

              <div className="section-gap" />
              <div className="section-heading">Accepted (In Kitchen) · {accepted.length}</div>
              {accepted.length === 0
                ? <div className="empty-state">No orders in kitchen</div>
                : accepted.map((o) => <OrderCard key={o.id} order={o} onAction={handleOrderAction} business={business} onSendBill={setBillOrder} />)}

              {served.length > 0 && (
                <>
                  <div className="section-gap" />
                  <div className="section-heading" style={{color:'#0ea5e9'}}>Served · {served.length}</div>
                  {served.map((o) => <OrderCard key={o.id} order={o} onAction={handleOrderAction} business={business} onSendBill={setBillOrder} />)}
                </>
              )}

              {paymentPending.length > 0 && (
                <>
                  <div className="section-gap" />
                  <div className="section-heading" style={{color:'#f59e0b'}}>Payment Pending · {paymentPending.length}</div>
                  {paymentPending.map((o) => <OrderCard key={o.id} order={o} onAction={handleOrderAction} business={business} onSendBill={setBillOrder} />)}
                </>
              )}

              {completed.length > 0 && (
                <>
                  <div className="section-gap" />
                  <div className="section-heading">Completed today · {completed.length}</div>
                  {completed.map((o) => <OrderCard key={o.id} order={o} onAction={handleOrderAction} business={business} onSendBill={setBillOrder} />)}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ══════ MENU ══════ */}
      {tab === 'menu' && (
        <div className="orders-section">
          <AddItemForm onAdd={handleAddItem} />
          {menuLoad ? <div className="loading">Loading menu…</div> : menuErr ? (
            <div style={{color:'#c0392b',padding:16,textAlign:'center'}}>{menuErr}</div>
          ) : (
            (() => {
              const knownOrder = new Map(CATEGORIES.map((c, i) => [c, i]));
              const allCats = [...new Set(menuItems.map((i) => i.category))].sort((a, b) => {
                const ai = knownOrder.has(a) ? knownOrder.get(a) : 999;
                const bi = knownOrder.has(b) ? knownOrder.get(b) : 999;
                return ai !== bi ? ai - bi : a.localeCompare(b);
              });
              return allCats.map((cat) => (
                <div key={cat}>
                  <div className="section-heading" style={{marginTop:16}}>{cat}</div>
                  {menuItems.filter((i) => i.category === cat).map((item) => (
                    <MenuItemRow key={item.id} item={item} onSave={handleSaveItem} onDelete={handleDeleteItem} />
                  ))}
                </div>
              ));
            })()
          )}
        </div>
      )}

      {/* ══════ SETTINGS ══════ */}
      {tab === 'settings' && business && (
        <ShopSettings business={business} onUpdate={refreshBusiness} />
      )}

      {/* ══════ STATS ══════ */}
      {tab === 'stats' && (
        <StatsTab orders={orders} />
      )}

      {/* ══════ TABLES ══════ */}
      {tab === 'tables' && business && (
        <TableMapTab orders={orders} business={business} />
      )}

      {/* ══════ INVENTORY ══════ */}
      {tab === 'inventory' && (
        <div className="orders-section">
          <InventoryTab />
        </div>
      )}

      {/* ══════ BILL MODAL ══════ */}
      {billOrder && business && (
        <BillModal
          order={billOrder}
          business={business}
          onClose={() => setBillOrder(null)}
        />
      )}
    </div>
  );
}
